/**
 * The customer context under the customer line of an agent case (sales review of
 * 12 Oct 2026, U-02): nine of the 29 cases came through an agent, a distributor or a
 * sister company, and the line showed only "Confirm <account>" or "Find customer". Who
 * sent the mail and for whom it asks sat in the Details sheet.
 *
 * One row with three labelled parts, read only: the account to quote (the name on the
 * line, or "none yet" while nobody is matched), the sender marked as the agent, and the
 * end customer as the mail names it (its evidence sentence as the title). The Confirm,
 * Find and Why controls stay on the customer line above; this block changes nothing.
 * Rendered only while the sender is an agent or a sister company and the case names an
 * agent or an end customer; every other case keeps the line alone.
 */

import { Icon } from "@norican/icons";
import { Badge, Typography } from "@norican/react";

import { stripCustomerOf } from "@/features/case-review/case-summary-strip";
import { PARTY_ICON } from "@/lib/line-icons";
import { type QuoteCaseDetail } from "@/lib/mock";
import { CUSTOMER_PARTY_LABELS, QUOTED_FIRST } from "@/lib/sales-flow";

/** The three parts of the block, or null when the case is not an agent case. */
export interface CustomerParties {
  /** The account's name as the customer line shows it; null while nobody is matched. */
  account: string | null;
  /** The account is a suggestion: nobody confirmed it and I2Q is not certain of it. */
  accountSuggested: boolean;
  /** The sender, who is the agent (name as the case knows it). */
  agent: string | null;
  /** The mark after the sender's name: "agent", or "sister company" for a group company. */
  senderMark: string;
  /** The end customer as the mail names it, with the sentence that names it. */
  endCustomer: { name: string; evidence: string | null } | null;
  /**
   * The party whose account is quoted first (hunt round 2, decision 2); null when the
   * server did not say. Both parties are always shown; this decides the order only.
   */
  quoteFirst: "agent" | "end_customer" | null;
  /** The two parties in the order the block shows them. */
  order: readonly ("agent" | "end_customer")[];
}

/** Pure: what the block shows for a case, or null when it shows nothing. */
export function customerPartiesOf(detail: QuoteCaseDetail): CustomerParties | null {
  const block = detail.customerBlock ?? null;
  if (!block || (block.role !== "agent" && block.role !== "sister_company")) {
    return null;
  }
  const agent = block.agent?.name?.trim() || null;
  const endName = block.end_customer?.name?.trim() || null;
  if (!agent && !endName) {
    return null;
  }
  const customer = stripCustomerOf(detail);
  const account =
    customer.state === "certain"
      ? customer.name || null
      : customer.state === "suggested"
        ? (customer.options[0]?.name ?? null)
        : null;
  const quoteFirst = block.quote_first ?? null;
  return {
    account,
    accountSuggested: account != null && customer.state === "suggested",
    agent,
    senderMark:
      block.role === "sister_company"
        ? CUSTOMER_PARTY_LABELS.sisterCompany
        : CUSTOMER_PARTY_LABELS.agent,
    endCustomer: endName
      ? { name: endName, evidence: block.end_customer?.evidence?.trim() || null }
      : null,
    quoteFirst,
    order: quoteFirst === "end_customer" ? ["end_customer", "agent"] : ["agent", "end_customer"],
  };
}

/** The "Quoted first" chip after the party whose account is quoted first. */
function QuotedFirst() {
  return (
    <Badge tone="info" variant="subtle" size="xs" data-party-mark="quoted-first">
      {QUOTED_FIRST}
    </Badge>
  );
}

function Part({
  label,
  children,
  title,
}: {
  label: string;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <span className="inline-flex min-w-0 items-baseline gap-1 text-xs" title={title}>
      <span className="text-fg-subtle">{label}:</span>
      <span className="text-fg min-w-0 font-semibold break-words">{children}</span>
    </span>
  );
}

/** The block under the customer line; null for a case that is not an agent case. */
export function CustomerParties({ detail }: { detail: QuoteCaseDetail }) {
  const parties = customerPartiesOf(detail);
  if (!parties) {
    return null;
  }
  return (
    <Typography
      as="div"
      variant="body-sm"
      className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5"
      data-customer-parties
      aria-label="Agent and end customer of this mail"
    >
      <Part label={CUSTOMER_PARTY_LABELS.account}>
        {parties.account ?? (
          <span className="text-fg-muted font-normal">{CUSTOMER_PARTY_LABELS.accountNone}</span>
        )}
        {parties.accountSuggested && (
          <span className="text-fg-muted font-normal" data-party-mark="suggested">
            {" "}
            ({CUSTOMER_PARTY_LABELS.accountSuggested})
          </span>
        )}
      </Part>
      {parties.order.map((party) =>
        party === "agent"
          ? parties.agent && (
              <Part key="agent" label={CUSTOMER_PARTY_LABELS.sender}>
                <span className="inline-flex items-center gap-1" data-party="agent">
                  <Icon icon={PARTY_ICON.agent} size="xs" className="shrink-0" />
                  {parties.agent}
                  <span className="text-fg-muted font-normal" data-party-mark="agent">
                    ({parties.senderMark})
                  </span>
                  {parties.quoteFirst === "agent" && <QuotedFirst />}
                </span>
              </Part>
            )
          : parties.endCustomer && (
              <Part
                key="end_customer"
                label={CUSTOMER_PARTY_LABELS.endCustomer}
                title={parties.endCustomer.evidence ?? CUSTOMER_PARTY_LABELS.endCustomerNote}
              >
                <span className="inline-flex items-center gap-1" data-party="end_customer">
                  <Icon icon={PARTY_ICON.endCustomer} size="xs" className="shrink-0" />
                  {parties.endCustomer.name}
                  <span className="text-fg-muted font-normal">
                    ({CUSTOMER_PARTY_LABELS.endCustomerNote})
                  </span>
                  {parties.quoteFirst === "end_customer" && <QuotedFirst />}
                </span>
              </Part>
            ),
      )}
    </Typography>
  );
}
